import * as z from "zod/v4";

export enum SesEventType {
  Bounce = "Bounce",
  Complaint = "Complaint",
  Delivery = "Delivery",
  Send = "Send",
  Open = "Open",
  Click = "Click",
  Subscription = "Subscription",
}

export enum SesClickTags {
  BLOCK_ID = "block_id",
  ELEMENT_ID = "element_id",
}

export const SesMailSchema = z.object({
  triggerType: z.string().nullish(),
  timestamp: z.string(),
  source: z.string().optional(),
  sourceArn: z.string().nullish(),
  sourceIp: z.string().nullish(),
  sendingAccountId: z.string().nullish(),
  callerIdentity: z.string().nullish(),
  messageId: z.string(),
  destination: z.array(z.string()).optional(),
  headersTruncated: z.boolean().nullish(),
  headers: z.array(z.object({ name: z.string(), value: z.string() })).nullish(),
  tags: z.record(z.string(), z.array(z.string())).optional(),
  commonHeaders: z
    .object({
      from: z.array(z.string()).optional(),
      to: z.array(z.string()).optional(),
      subject: z.string().optional(),
      date: z.string().nullish(),
      messageId: z.string().nullish(),
    })
    .optional(),
});

export const SesBounceEventSchema = z.object({
  eventType: z.literal(SesEventType.Bounce),
  mail: SesMailSchema,
  bounce: z.object({
    bounceType: z.string(),
    bounceSubType: z.string(),
    bouncedRecipients: z.array(
      z.object({
        emailAddress: z.string(),
        status: z.string().optional(),
        action: z.string().optional(),
        diagnosticCode: z.string().optional(),
      }),
    ),
    timestamp: z.string(),
    feedbackId: z.string(),
    reportingMTA: z.string().nullish(),
  }),
});

export const SesComplaintEventSchema = z.object({
  eventType: z.literal(SesEventType.Complaint),
  mail: SesMailSchema,
  complaint: z.object({
    complainedRecipients: z.array(z.object({ emailAddress: z.string() })),
    timestamp: z.string(),
    feedbackId: z.string(),
    complaintSubType: z.string().nullish(),
    userAgent: z.string().optional(),
    complaintFeedbackType: z.string().optional(),
    arrivalDate: z.string().nullish(),
  }),
});

export const SesDeliveryEventSchema = z.object({
  eventType: z.literal(SesEventType.Delivery),
  mail: SesMailSchema,
  delivery: z.object({
    timestamp: z.string(),
    processingTimeMillis: z.number(),
    recipients: z.array(z.string()),
    smtpResponse: z.string(),
    reportingMTA: z.string(),
    remoteMtaIp: z.string().optional(),
  }),
});

export const SesSendEventSchema = z.object({
  eventType: z.literal(SesEventType.Send),
  mail: SesMailSchema,
});

export const SesOpenEventSchema = z.object({
  eventType: z.literal(SesEventType.Open),
  mail: SesMailSchema,
  open: z.object({
    timestamp: z.string(),
    ipAddress: z.string(),
    userAgent: z.string(),
  }),
});

export const SesClickEventSchema = z.object({
  eventType: z.literal(SesEventType.Click),
  mail: SesMailSchema,
  click: z.object({
    timestamp: z.string(),
    link: z.string(),
    userAgent: z.string(),
    ipAddress: z.string(),
    linkTags: z
      .record(z.string(), z.array(z.string()))
      .optional()
      .catch(undefined), // remove this line once we're 100% confident this is the right format
  }),
});

export const SesSubscriptionEventSchema = z.object({
  eventType: z.literal(SesEventType.Subscription),
  mail: SesMailSchema,
  subscription: z.object({
    contactList: z.string(),
    timestamp: z.string(),
    source: z.string(),
    newTopicPreferences: z.object({
      unsubscribeAll: z.boolean(),
      topicSubscriptionStatus: z.array(
        z.object({ topicName: z.string(), subscriptionStatus: z.string() }),
      ),
    }),
    oldTopicPreferences: z.object({
      unsubscribeAll: z.boolean(),
      topicSubscriptionStatus: z.array(
        z.object({ topicName: z.string(), subscriptionStatus: z.string() }),
      ),
    }),
  }),
});

export type SesSendEvent = z.infer<typeof SesSendEventSchema>;
export type SesOpenEvent = z.infer<typeof SesOpenEventSchema>;
export type SesClickEvent = z.infer<typeof SesClickEventSchema>;
export type SesSubscriptionEvent = z.infer<typeof SesSubscriptionEventSchema>;
export type SesBounceEvent = z.infer<typeof SesBounceEventSchema>;
export type SesComplaintEvent = z.infer<typeof SesComplaintEventSchema>;
export type SesDeliveryEvent = z.infer<typeof SesDeliveryEventSchema>;

export const SesEventSchema = z.discriminatedUnion("eventType", [
  SesBounceEventSchema,
  SesComplaintEventSchema,
  SesDeliveryEventSchema,
  SesSendEventSchema,
  SesOpenEventSchema,
  SesClickEventSchema,
  SesSubscriptionEventSchema,
]);

export type SesEvent = z.infer<typeof SesEventSchema>;
